import type { GetStaticPaths, GetStaticProps } from 'next'
import type { FC } from 'react'
import styled from 'styled-components'

import {
  getCategoryBySlug,
  getSubCategoriesByID,
  getTopAcademyCategories,
  latestArticlesByCategory,
} from '@/graphql/HealthAcademyCategories'

import { Footer } from '@/components/elements/footer/Footer'
import { Header } from '@/components/elements/header/Header'
import { ArticlesByCategory } from '@/components/pages/learn/Article/ArticlesByCategory'
import { Container } from '@/components/shared/Container'
import { SEOMetaTags } from '@/components/shared/SEOMetaTags'

import { SubNavigation } from '@/elements/global/SubNavigation'

// Derived from the GraphQL helpers so the shapes stay in step with the queries.
type CategoryContentProps = {
  categories: Awaited<ReturnType<typeof getTopAcademyCategories>>
  category: Awaited<ReturnType<typeof getCategoryBySlug>>
  articlesByCategory: Array<Awaited<ReturnType<typeof latestArticlesByCategory>>>
  subcategories: Awaited<ReturnType<typeof getSubCategoriesByID>>
  slug: string
}

const StyledIntro = styled.div`
  text-align: center;
  margin: 80px 0;
  h1 {
    font-family: ${(props) => props.theme.v2.font.nantes};
  }
  @media screen and (max-width: ${(props) => props.theme.breakpoints.mobile}) {
    margin: 40px 0 60px;
  }
`

const Index: FC<CategoryContentProps> = ({ categories, category, articlesByCategory, subcategories, slug }) => {
  const { categoryName, categorySlug, description, metadata } = category

  return (
    <>
      <SEOMetaTags metaData={metadata ?? undefined} />
      <Header />
      <SubNavigation title="Learn" categories={categories} selected={slug} />
      <main id="main-content">
        <StyledIntro>
          <Container>
            <h1>{categoryName}</h1>
            <p>{description}</p>
          </Container>
        </StyledIntro>
        {subcategories.map((subcategory, categoryIndex) => {
          const articles = articlesByCategory[categoryIndex]
          return (
            <ArticlesByCategory
              key={subcategory.id}
              categoryID={subcategory.id}
              categoryName={subcategory.categoryName}
              categorySlug={categorySlug}
              subCategorySlug={subcategory.categorySlug}
              articles={articles}
              showHeader={true}
            />
          )
        })}
      </main>
      <Footer showPreFooter />
    </>
  )
}

export default Index

export const getStaticPaths: GetStaticPaths = async () => {
  const categories = await getTopAcademyCategories()
  const categoryPaths = categories.map((category) => `/learn/category/${category.categorySlug}`)
  const paths = categoryPaths

  return { paths, fallback: false }
}

export const getStaticProps: GetStaticProps<CategoryContentProps> = async ({ params, preview }) => {
  const slug: string = typeof params?.slug === 'string' ? params.slug : ''

  const categories = await getTopAcademyCategories()
  const category = await getCategoryBySlug(slug, preview)
  const subcategories = await getSubCategoriesByID(category.id, preview)
  const promiseArray = subcategories.map((subcategory) => latestArticlesByCategory(subcategory.id))
  const articlesByCategory = await Promise.all(promiseArray).then((values) => values)

  return { props: { categories, category, articlesByCategory, subcategories, slug } }
}
